import type { AttachmentType } from '@fiverr-private/inspire_listings/src/types/inspire';
import { getContext } from '@fiverr-private/fiverr_context';
import { bigQuery } from '@fiverr-private/obs';

export interface BigQueryEvents {
    trackPageView: () => void;
    trackClickOnSellerName: ({ sellerUserName }: { sellerUserName: string }) => void;
    trackClickOnContactSeller: () => void;
    trackClickOnSeeGig: () => void;
    trackClickOnSaveDelivery: () => void;
}

export interface BigQueryCommonProps {
    deliveryId: string;
    deliveryAttachmentType: AttachmentType;
    gigId: string;
    sellerId: string;
    categoryId: number;
    subCategoryId: number;
    nestedSubCategoryId?: number;
}

export const createEvents = (commonProps: BigQueryCommonProps): BigQueryEvents => ({
    trackPageView: () => {
        send({
            ...commonProps,
            type: 'delivery_page_is_shown',
            group: 'page_view',
            actionType: 'page_view',
        });
    },
    trackClickOnSellerName: ({ sellerUserName }) => {
        send({
            ...commonProps,
            type: 'clicked_on_seller_name',
            group: 'user_actions',
            actionType: 'click',
            pageElement: {
                type: 'link',
                text: sellerUserName,
                name: 'seller_title',
            },
            pageComponent: {
                name: 'seller_info',
            },
            eventSource: 'seller_info',
        });
    },
    trackClickOnContactSeller: () => {
        send({
            ...commonProps,
            type: 'clicked_on_contact_seller',
            group: 'user_actions',
            actionType: 'click',
            pageElement: {
                type: 'link',
                text: 'Contact seller',
                name: 'contact_seller',
            },
            pageComponent: {
                name: 'seller_info',
            },
            eventSource: 'seller_info',
        });
    },
    trackClickOnSeeGig: () => {
        send({
            ...commonProps,
            type: 'clicked_on_see_gig',
            group: 'user_actions',
            actionType: 'click',
            pageElement: {
                type: 'link',
                text: 'See Gig',
                name: 'see_gig',
            },
            pageComponent: {
                name: 'seller_info',
            },
            eventSource: 'seller_info',
        });
    },
    trackClickOnSaveDelivery: () => {
        send({
            ...commonProps,
            type: 'clicked_on_save_delivery',
            group: 'user_actions',
            actionType: 'click',
            pageElement: {
                type: 'button',
                text: 'favorite',
                name: 'add_to_list',
            },
            pageComponent: {
                name: 'delivery_preview',
            },
            eventSource: 'delivery_preview',
        });
    },
});

const send = ({
    deliveryId,
    deliveryAttachmentType,
    gigId,
    sellerId,
    categoryId,
    subCategoryId,
    nestedSubCategoryId,
    type,
    group,
    actionType,
    pageElement,
    pageComponent,
    eventSource,
}: BigQueryCommonProps & {
    type: string;
    group: 'page_view' | 'user_actions';
    actionType: 'page_view' | 'click';
    pageElement?: {
        type: 'link' | 'button';
        text: string;
        name: string;
    };
    pageComponent?: {
        name: string;
    };
    eventSource?: string;
}): void => {
    const { pageCtxId } = getContext();

    bigQuery.send({
        type,
        group,
        sub_group: 'inspiration',
        seller: { id: sellerId },
        gig: { id: gigId },
        category: { id: categoryId },
        sub_category: { id: subCategoryId },
        nested_sub_category: { id: nestedSubCategoryId },
        inspire_delivery: { id: deliveryId, type: deliveryAttachmentType },
        action: { type: actionType },
        page: {
            name: 'delivery_page',
            ctx_id: pageCtxId,
            ...(pageElement ? { element: pageElement } : {}),
            ...(pageComponent ? { component: pageComponent } : {}),
        },
        ...(eventSource ? { event_source: eventSource } : {}),
    });
};
